import React, { createContext, useContext, useEffect, useState } from 'react';
import { requestNotificationPermission, onMessageListener, getCurrentToken } from '../services/firebase';

interface NotificationContextType {
  permission: NotificationPermission;
  token: string | null;
  requestPermission: () => Promise<void>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize permission state ONCE on app load
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
      
      // Try to get existing token if permission already granted
      if (Notification.permission === 'granted') {
        getCurrentToken().then(existingToken => {
          if (existingToken) {
            setToken(existingToken);
          }
        });
      }
    }
  }, []);

  // Request notification permission
  const requestPermission = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const fcmToken = await requestNotificationPermission();
      
      if (fcmToken) {
        setToken(fcmToken);
        setPermission('granted');
      } else {
        setPermission(Notification.permission);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setPermission(Notification.permission);
    } finally {
      setIsLoading(false);
    }
  };

  // Listen for foreground messages
  useEffect(() => {
    if (permission === 'granted') {
      onMessageListener()
        .then((payload: any) => {
          console.log('Foreground notification received:', payload);
          
          // Show browser notification for foreground messages
          if (payload.notification) {
            new Notification(payload.notification.title || 'Nueva notificación', {
              body: payload.notification.body,
              icon: '/logo.png',
              tag: payload.data?.tag || 'default'
            });
          }
        })
        .catch((err) => console.error('Error listening to messages:', err));
    }
  }, [permission]);

  return (
    <NotificationContext.Provider value={{ permission, token, requestPermission, isLoading }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};
